/* eslint-disable no-redeclare */
import type { CampaignPlacement, Device, YesNo } from '@hc-frontend/core-types';
import {
  FeaturedAds,
  Gender,
  ProductType,
  TobaccoUse,
} from '@hc-frontend/core-types';
import { qualifyLifeEventCodeToQuotIt } from '@hc-frontend/core-types-transformers';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { v1 } from '@hc-frontend/services-featured-ad';
import { z } from 'zod';

import {
  type FeatureAdsResult,
  type FeaturedAdsRepository,
} from '../../domain';

const mapDtoToEntity = (data: v1.FeaturedAdDto): FeaturedAds => ({
  id: data.Id,
  searchMethod: data.SearchMethod,
  segmentationFit: data.SegmentationFit,
  result: data.Result.filter(
    (result) => result.Delivery.Click.InterstitialUrl !== null,
  ).map((result) => ({
    id: result.AdsID,
    title: result.AdCreative.Title,
    logo: result.AdCreative.Image.startsWith('//')
      ? `https:${result.AdCreative.Image}`
      : result.AdCreative.Image,
    campaignId: result.IdCampaign,
    content: result.AdCreative.Snippet,
    cpc: result.Delivery.Click.Price,
    creativeId: result.AdCreative.Id,
    dealType: result.DealType,
    link: result.Delivery.Click.InterstitialUrl as string,
    provider: {
      name: result.AdvertiserName,
      displayUrl: result.AdCreative.DisplayUrl.startsWith('http')
        ? new URL(result.AdCreative.DisplayUrl).hostname
        : result.AdCreative.DisplayUrl,
      id: result.AdvertiserID,
    },
    campaignName: result.CampaignName,
    groupId: result.AdGroupId,
  })),
});

async function getFeaturedAdsAndValidate(
  baseUrl: string,
  payload: v1.FeaturedAdsListPayloadDto,
  snowplowData?: string,
): Promise<FeatureAdsResult> {
  const result = await v1.getFeaturedAdsList(baseUrl, payload, snowplowData);

  if (result.status !== 200) {
    throw new Error(`Failed to fetch featured ads with code ${result.status}`);
  }

  const parsedFeaturedAdsDto = v1.FeaturedAdDto.parse(result.body);

  const mappedFeaturedAdsEntity = mapDtoToEntity(parsedFeaturedAdsDto);
  const parsedFeaturedAds = FeaturedAds.parse(mappedFeaturedAdsEntity);

  return createSuccessOperationResult(parsedFeaturedAds);
}

const AllowedSetups = z.object({
  [ProductType.Enum.HEALTH]: z.enum(['TYP', 'EMAIL', 'SURVEY']),
  [ProductType.Enum.MEDICARE]: z.enum([
    'TYP',
    'EMAIL',
    'SURVEY',
    'TYP_AARP_CLICK',
  ]),
  [ProductType.Enum.MEDICARE_ADVANTAGE]: z.enum(['SURVEY', 'TYP']),
});

type AllowedSetups = z.infer<typeof AllowedSetups>;

const getApiConfig = (
  product: ProductType,
  placement: CampaignPlacement,
  device: Device,
) => {
  const parsedProduct = ProductType.parse(product);
  const parsedPlacement = AllowedSetups.shape[parsedProduct].parse(placement);

  const config = {
    [ProductType.Enum.HEALTH]: {
      TYP: {
        PublisherCampaignId: 14,
        product: ProductType.Enum.HEALTH,
        hc_product_type: 1,
        hc_placement: 'TYP',
        device,
      },
      EMAIL: {
        PublisherCampaignId: 0,
        product: ProductType.Enum.HEALTH,
        hc_product_type: 1,
        hc_placement: 'EMAIL',
        device,
      },
      SURVEY: {
        PublisherCampaignId: 0,
        product: ProductType.Enum.HEALTH,
        hc_product_type: 1,
        hc_placement: 'SURVEY',
        device,
      },
    },
    [ProductType.Enum.MEDICARE]: {
      TYP: {
        PublisherCampaignId: 24,
        product: ProductType.Enum.MEDICARE,
        hc_product_type: 2,
        hc_placement: 'TYP',
      },
      EMAIL: {
        PublisherCampaignId: 0,
        product: ProductType.Enum.MEDICARE,
        hc_product_type: 2,
        hc_placement: 'EMAIL',
      },
      SURVEY: {
        PublisherCampaignId: 0,
        product: ProductType.Enum.MEDICARE,
        hc_product_type: 2,
        hc_placement: 'SURVEY',
      },
      TYP_AARP_CLICK: {
        PublisherCampaignId: 24,
        product: ProductType.Enum.MEDICARE,
        hc_product_type: 2,
        hc_placement: 'TYP',
      },
    },
    [ProductType.Enum.MEDICARE_ADVANTAGE]: {
      SURVEY: {
        PublisherCampaignId: 0,
        product: ProductType.Enum.MEDICARE_ADVANTAGE,
        hc_product_type: 4,
        hc_placement: 'SURVEY',
      },
      TYP: {
        PublisherCampaignId: 0,
        product: ProductType.Enum.MEDICARE_ADVANTAGE,
        hc_product_type: 4,
        hc_placement: 'TYP',
      },
    },
  } as const satisfies {
    [key in keyof AllowedSetups]: {
      [subKey in AllowedSetups[key]]: unknown;
    };
  };

  const setup = config[product as keyof AllowedSetups];

  return setup[parsedPlacement as keyof typeof setup];
};

export function featuredAdsAdapterFactory(
  baseUrl: string,
): FeaturedAdsRepository {
  return {
    async getFeaturedAds(options) {
      try {
        const { cookies } = await import('next/headers');

        const config = getApiConfig(
          options.config.productType,
          options.config.placement,
          options.user.device,
        );

        const { dob, gender } = options.applicant;

        const payload: v1.FeaturedAdsListPayloadDto = {
          hc_maxregs: options.config.limit,
          hc_state: options.location.stateAcronym,
          hc_zip: options.location.zipCode,
          hc_qle: qualifyLifeEventCodeToQuotIt(
            options.qualification.qualifyLifeEvent,
          ),
          hc_household_income: options.qualification.household.income,
          hc_household_size: options.qualification.household.size,
          hc_children_amount: options.applicant.childrenCount,
          hc_beneficiary_type: 'member',
          hc_subsidy_eligibility:
            options.qualification.subsidyType === 'SubsidyEligible',
          hc_session: options.healthcare.sessionId,
          uid: options.healthcare.userId,
          PlansSearchSessionId: options.healthcare.sessionId,
          utm_source: options.campaign.utmSource ?? '',
          mapped_source: options.tracking.source,
          src: options.tracking.source,
          subid: options.tracking.subid?.toString() ?? '',
          bt_sid: options.tracking.btSid || '',
          irpid: options.tracking.ircid || options.tracking.irpid || '',
          goal_url: '',
          hc_plan_search_id: '',
          hc_applicant_dob: `${dob.getFullYear()}/${(dob.getMonth() + 1).toString().padStart(2, '0')}/${dob.getDate().toString().padStart(2, '0')}`,
          hc_applicant_gender: gender === Gender.Enum.MALE ? 1 : 2,
          hc_smoker_applicant: (options.applicant.tobacco ===
          TobaccoUse.Enum.YES
            ? 'Y'
            : 'N') as Uppercase<YesNo>,
          branded_campaigns: options.qualification.brandedCampaignId,
          ...config,
        };

        return await getFeaturedAdsAndValidate(
          baseUrl,
          payload,
          (await cookies())
            .getAll()
            .find((cookie) => cookie.name.startsWith('_sp_id'))?.value,
        );
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToFetchFeaturedAds',
              rootCause: error as Error,
            },
          ],
        });
      }
    },
  };
}
